import {useEffect, useState} from 'react';
import {observer} from 'mobx-react';
import {useLocation} from 'react-router-dom';
import AppStore from '../../app.store';
import Navbar from '../navbar/navbar';
import Header from '../header';
import Routes from '../../routes';
import AppBootstrapScreen from '../app-bootstrap-screen';

type OverlayPhase = 'hidden' | 'visible' | 'fading';

/**
 * Authenticated layout shell. Subscribes only to auth/bootstrap fields —
 * resize, network, and nav toggles stay in Navbar/Header observers.
 */
const AppAuthenticatedShell = observer(() => {
    const {isLoggedIn, isAppReady, userData, bootstrapDisplayName, bootstrapMode} = AppStore;
    const {pathname} = useLocation();
    const isOnboarding = pathname === '/onboarding';
    const isWelcomeBootstrap = bootstrapMode === 'welcome';
    const displayName = userData?.name || bootstrapDisplayName;
    const [overlayPhase, setOverlayPhase] = useState<OverlayPhase>(() =>
        isLoggedIn && !isAppReady && isWelcomeBootstrap ? 'visible' : 'hidden',
    );

    useEffect(() => {
        if (!isLoggedIn) {
            setOverlayPhase('hidden');
            return;
        }

        if (!isWelcomeBootstrap) {
            setOverlayPhase('hidden');
            return;
        }

        if (!isAppReady) {
            setOverlayPhase('visible');
            return;
        }

        const frame = window.requestAnimationFrame(() => {
            setOverlayPhase('fading');
        });

        return () => window.cancelAnimationFrame(frame);
    }, [isAppReady, isLoggedIn, isWelcomeBootstrap]);

    if (!isLoggedIn) {
        return null;
    }

    const showShell = isAppReady || (isWelcomeBootstrap && overlayPhase === 'fading');
    const showWelcomeOverlay = isWelcomeBootstrap && (overlayPhase === 'visible' || overlayPhase === 'fading');
    const showSilentLoader = !isWelcomeBootstrap && !isAppReady;

    return (
        <>
            {showShell && (
                <>
                    {!isOnboarding && <Navbar />}
                    {!isOnboarding && <Header />}
                    <Routes />
                </>
            )}
            {showWelcomeOverlay && !isOnboarding && (
                <AppBootstrapScreen
                    displayName={displayName}
                    overlay
                    fading={overlayPhase === 'fading'}
                    onFadeComplete={() => setOverlayPhase('hidden')}
                />
            )}
            {showSilentLoader && (
                <AppBootstrapScreen
                    variant="silent"
                    overlay
                    rotateSubtitles={false}
                />
            )}
        </>
    );
});

export default AppAuthenticatedShell;
